import { addServiceDays, isServiceDay } from '@croissants/contracts/projections';
import {
  DEFAULT_TIME_ZONE,
  formatBasisPoints,
  formatDate,
  formatMoney,
  formatRelativeTime,
  formatShortDateTime,
  formatShortDay,
  formatShortServiceDay,
  formatSignedMoney,
  formatTime,
  formatWindow,
  type Locale,
  toServiceDay,
} from '@croissants/i18n';

/**
 * Formatting for the operations portal.
 *
 * Two rules hold everywhere here and both come from the platform, not from a
 * component: money crosses every boundary as integer minor units and is divided
 * exactly once at the last moment (PRD NFR-003), and an instant is rendered in
 * the operational location's timezone rather than the reader's - an operator in
 * another zone must still see the Paris morning (tech-stack section 8).
 */

/** The zone the service runs in. Every screen renders wall-clock time in it. */
export const OPERATIONS_TIME_ZONE = DEFAULT_TIME_ZONE;

export function money(minorUnits: number, currency: string, locale: Locale): string {
  return formatMoney(minorUnits, currency, locale);
}

/**
 * A figure that depends on what the guest picks, at both ends.
 *
 * A formule's bullet offering a choice costs what the guest takes, and what a
 * house keeps differs per product even where the guest's price does not - so
 * the platform is handed a `{ min, max }` pair rather than a number
 * (docs/plans/mode-a-menu-and-pricing.html section 7). When the ends agree
 * there was no choice to make, and one number is what a reader should see.
 *
 * Both ends come off the wire already computed and already oriented; nothing
 * here adds, subtracts or re-orders them.
 */
export function moneyRange(
  range: { readonly min: number; readonly max: number },
  currency: string,
  locale: Locale,
): string {
  const low = formatMoney(range.min, currency, locale);
  if (range.min === range.max) return low;
  return `${low} – ${formatMoney(range.max, currency, locale)}`;
}

/**
 * A set's margin at its CHEAPEST basket, then at its DEAREST - the order the
 * price ranges beside it read (founder, 2026-09-02: "the order of the margin is
 * the same as the price order"), and never sorted by size: a set whose dearest
 * basket leaves less reads high to low, and that is the information. One
 * figure when the two agree. `moneyRange` sorts; this deliberately does not.
 */
export function moneyAtBaskets(
  pair: { readonly atDearest: number; readonly atCheapest: number },
  currency: string,
  locale: Locale,
): string {
  const cheapest = formatMoney(pair.atCheapest, currency, locale);
  if (pair.atDearest === pair.atCheapest) return cheapest;
  return `${cheapest} – ${formatMoney(pair.atDearest, currency, locale)}`;
}

/** The same order for the two rates, rounded first and compared after. */
export function percentWholeAtBaskets(
  pair: { readonly atDearest: number; readonly atCheapest: number },
  locale: Locale,
): string {
  const cheapest = percentWhole(pair.atCheapest, locale);
  const dearest = percentWhole(pair.atDearest, locale);
  return dearest === cheapest ? cheapest : `${cheapest} – ${dearest}`;
}

/** Signed money, for a drift or a fee divergence where the direction is the point. */
export const signedMoney = formatSignedMoney;

/**
 * One line's share of a total - "43.2 %".
 *
 * A RATIO between two figures the platform was handed, not an amount: it
 * settles nothing, moves nothing and creates no cent, which is why it is
 * computed on the surface that reads it while every euro figure beside it comes
 * off the one money view (`scripts/check-money-derivation.mjs`).
 *
 * One decimal, fixed. The financials card is a column of these read against
 * each other, so they have to line up on the same digit; two decimals adds a
 * figure nobody compares, and none collapses the three smallest rows of a
 * typical order to the same "0 %".
 */
export function share(part: number, whole: number, locale: Locale): string {
  if (whole === 0) return '';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(part / whole);
}

export function percent(basisPoints: number, locale: Locale): string {
  return formatBasisPoints(basisPoints, locale);
}

/**
 * A margin as WHOLE percent - "37 %", never "38.8 %".
 *
 * The Menus tab prints this under the euro figure, on a row a curator SCANS
 * down a column of forty; two decimal places there is a second number to read
 * and says nothing a negotiation turns on. The euro amount above it is the
 * exact one, to the cent.
 *
 * Rounding happens on the RATIO, in `marginOnPrices` (packages/pricing), and
 * this only chooses how much of it to print - which is why it takes basis
 * points rather than two prices.
 */
export function percentWhole(basisPoints: number, locale: Locale): string {
  return formatBasisPoints(Math.round(basisPoints / 100) * 100, locale);
}

export function time(instant: string | Date | number, locale: Locale): string {
  return formatTime(instant, locale, OPERATIONS_TIME_ZONE);
}

/**
 * "Jeu. 03/09 10:14" - THE moment a table prints, on every portal (founder,
 * 2026-09-03: a date and a time read the same way in every table). It was the
 * long "jeudi 3 septembre 10:14" here while the partner portal printed the day
 * and the time on two lines, which is two formats for one fact on two screens.
 */
export function dateTime(instant: string | Date | number, locale: Locale): string {
  return formatShortDateTime(instant, locale, OPERATIONS_TIME_ZONE);
}

/**
 * "Wed 30/09 19:11:52" - `dateTime` to the SECOND, for a log read in order
 * (founder, 1 October 2026: "the timeline should reflect correctly what is
 * happening and at what time it has been triggered").
 *
 * A minute is too coarse for an order's own history: the courier booking of
 * demo order VGBPHF was five provider calls and two rows inside thirteen
 * seconds, all of them printing "19:11" or "19:12", so the line order was the
 * only thing that said which came first - and the line order was what was
 * wrong. A table of orders keeps `dateTime`; a timeline prints this.
 */
export function preciseDateTime(instant: string | Date | number, locale: Locale): string {
  return `${formatShortDay(instant, locale, OPERATIONS_TIME_ZONE)} ${formatDate(
    instant,
    locale,
    OPERATIONS_TIME_ZONE,
    { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' },
  )}`;
}

/**
 * How long a provider call took, in the unit that reads: "850 ms" under a
 * second, "4.1 s" from one second up - the figure an operator compares against
 * the provider's own timeout, not a count of milliseconds to divide by eye.
 */
export function callDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function day(instant: string | Date | number, locale: Locale): string {
  return formatDate(instant, locale, OPERATIONS_TIME_ZONE);
}

/**
 * "Jeu. 03/09" - the day for a COLUMN of dates, and the day half of `dateTime`.
 *
 * `day()` spells "Friday 4 September", which is right for a sentence and is
 * wide in a table cell; a column read down, one date under another, wants the
 * tokens that tell the rows apart and nothing else, in the one shape every
 * table on every portal prints (founder, 2026-09-03). It said "Friday,
 * September 4" until 2026-09-06 - the month-first shape English surfaces
 * stopped printing when every date went day-first (#1052) - which is a quoted
 * example that had become a description of a format this portal no longer has.
 */
export function shortDay(instant: string | Date | number, locale: Locale): string {
  return formatShortDay(instant, locale, OPERATIONS_TIME_ZONE);
}

/**
 * "10/09/2026" - the day a record came into being, day-first with the year
 * (founder, 2026-09-30): a creation date is a date to read and compare, not a
 * weekday, and without the year a client from last September reads as new.
 */
export function numericDay(instant: string | Date | number, locale: Locale): string {
  return formatDate(instant, locale, OPERATIONS_TIME_ZONE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** "August 2026" - a whole calendar month named as one, for a monthly period. */
export function monthYear(instant: string | Date | number, locale: Locale): string {
  return formatDate(instant, locale, OPERATIONS_TIME_ZONE, { month: 'long', year: 'numeric' });
}

/**
 * "2 hours ago" - a distance, for a column an operator SCANS rather than reads.
 *
 * It carries no timezone because a distance has none, and it is never the whole
 * answer: every screen that prints one puts `dateTime()` on the element's
 * `title`, so the exact instant is one hover away for the telephone call.
 *
 * `now` comes from `GET /internal/time` (TST-003, OPS-001), never from the
 * browser - the same rule as `todayServiceDay` below.
 */
export function relativeTime(instant: string | Date | number, now: Date, locale: Locale): string {
  return formatRelativeTime(instant, now, locale);
}

/**
 * "3s", "3mn", "2h", "25d" - the same distance as `relativeTime`, as a figure.
 *
 * For the fleet list (founder, 2026-09-18), where "2 seconds ago" was the
 * widest thing in a column of figures and was cut to "2 seconds ..." at a
 * laptop's width. Truncated rather than rounded, as `relativeTime` is, and an
 * instant ahead of `now` - a counter's clock a second fast - reads "0s" rather
 * than a negative age. The exact instant stays on the cell's `title`.
 */
export function shortAge(instant: string | Date | number, now: Date): string {
  const seconds = Math.max(0, Math.floor((now.getTime() - new Date(instant).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}mn`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}

/**
 * A SERVICE DAY (`YYYY-MM-DD`) as a date a reader recognises.
 *
 * Anchored at noon UTC, exactly as `shiftServiceDay` anchors its arithmetic: a
 * service day is a calendar day in Europe/Paris, not an instant, and parsing it
 * bare gives UTC midnight - which any zone west of Greenwich renders as the day
 * before. Noon is far enough from both edges that no offset can move it.
 */
export function serviceDate(serviceDay: string, locale: Locale): string {
  return formatShortServiceDay(serviceDay, locale);
}

export function window(
  start: string | Date | number,
  end: string | Date | number,
  locale: Locale,
): string {
  return formatWindow(start, end, locale, OPERATIONS_TIME_ZONE);
}

/**
 * A given instant's service day in the operational zone: YYYY-MM-DD.
 *
 * `now` is REQUIRED, and there is deliberately no `new Date()` default: the
 * platform's "today" comes from `GET /internal/time` via `usePlatformDay()` (TST-003,
 * OPS-001), never from the operator's browser clock. Outside production the
 * platform runs on the rehearsal clock, and a browser-dated default is how the
 * admin dashboard once opened on an empty board with 34 orders in flight.
 */
export function todayServiceDay(now: Date): string {
  return toServiceDay(now, OPERATIONS_TIME_ZONE);
}

/**
 * Shifts a YYYY-MM-DD service day by whole days without touching a timezone.
 *
 * The arithmetic is `addServiceDays`, the platform's one answer. What this adds
 * is LENIENCE, and only this app wants it: every caller is a filter default
 * reading `platformToday`, which is the empty string until `GET /internal/time`
 * lands, and a screen that threw mid-render on a day it had not received yet
 * would be a white page instead of an empty filter.
 */
export function shiftServiceDay(serviceDay: string, days: number): string {
  return isServiceDay(serviceDay) ? addServiceDays(serviceDay, days) : serviceDay;
}

/** An integer inside a closed range, or null when the box does not hold one. */
export function intIn(raw: string, min: number, max: number): number | null {
  const value = Number.parseInt(raw, 10);
  if (!Number.isInteger(value) || String(value) !== raw.trim()) return null;
  return value >= min && value <= max ? value : null;
}

/**
 * A span of seconds as a human reads one on a debug screen: `38 s`,
 * `3 min 30 s`, `1 h 05 min`. Seconds are kept under an hour because the
 * timing table is about whether a courier was ten seconds or ten minutes past
 * a deadline, and `0 min` said nothing. Internal Ops is English-only, so the
 * units are written here rather than looked up.
 */
export function duration(totalSeconds: number): string {
  const seconds = Math.abs(Math.round(totalSeconds));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return `${hours} h ${String(rest).padStart(2, '0')} min`;
  }
  const days = Math.floor(hours / 24);
  return `${days} d ${String(hours % 24).padStart(2, '0')} h`;
}
